import React, { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import Particles from "./Particles";

export default function Hero({ settings, onOpenQuote }) {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 200]);
  const scale = useTransform(scrollY, [0, 800], [1, 1.15]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.15]);

  const line1 = settings?.hero_title_line1 || "MAIS QUE UM JOGO.";
  const line2 = settings?.hero_title_line2 || "UMA EXPERIÊNCIA REAL.";
  const image =
    settings?.hero_image_url ||
    "https://images.unsplash.com/photo-1677522375397-b7a40324dfb3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[720px] w-full overflow-hidden grain" data-testid="hero-section">
      {/* Background image parallax */}
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={image} alt="SIM EXPERIENCE" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,#090909_85%)]" />
      </motion.div>

      {/* Particles overlay */}
      <div className="absolute inset-0">
        <Particles density={70} />
      </div>

      {/* Main content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 h-full flex items-center px-6 md:px-14 pt-28 pb-12"
      >
        <div className="max-w-[1400px] mx-auto w-full grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-9">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-[1px] w-14 bg-[#E31B23]" />
              <span className="overline text-neutral-300">Aluguel de simuladores premium</span>
            </motion.div>

            <h1 className="font-display font-black leading-[0.95] tracking-tight">
              <motion.span
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="block text-white text-4xl sm:text-5xl md:text-6xl lg:text-[3.75rem] xl:text-[4.5rem] 2xl:text-[5rem]"
              >
                {line1}
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="block text-4xl sm:text-5xl md:text-6xl lg:text-[3.75rem] xl:text-[4.5rem] 2xl:text-[5rem] bg-clip-text text-transparent"
                style={{ backgroundImage: "linear-gradient(90deg, #E31B23 0%, #E31B23 55%, #008CFF 100%)" }}
              >
                {line2}
              </motion.span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-8 max-w-2xl text-neutral-300 text-base md:text-lg leading-relaxed"
            >
              {settings?.hero_subtitle || "Simuladores profissionais que transformam eventos em experiências inesquecíveis."}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.95 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={onOpenQuote}
                className="group inline-flex items-center gap-3 rounded-full bg-[#E31B23] px-8 py-4 text-sm md:text-base font-semibold uppercase tracking-wider text-white glow-red"
                data-testid="hero-quote-btn"
              >
                Solicitar orçamento
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#simuladores"
                className="group inline-flex items-center gap-3 rounded-full border border-[#008CFF]/60 px-8 py-4 text-sm md:text-base font-semibold uppercase tracking-wider text-white glow-blue hover:border-[#008CFF]"
                data-testid="hero-simulators-btn"
              >
                Conheça nossos simuladores
                <ChevronDown className="w-5 h-5 group-hover:translate-y-1 transition-transform" />
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="hidden lg:flex lg:col-span-3 flex-col gap-3"
          >
            <div className="glass rounded-2xl p-5">
              <div className="overline text-[#008CFF]">Motion</div>
              <div className="font-display text-2xl font-bold mt-1">6DOF</div>
              <div className="text-xs text-neutral-400 mt-1">Plataforma profissional</div>
            </div>
            <div className="glass rounded-2xl p-5">
              <div className="overline text-[#E31B23]">Direct</div>
              <div className="font-display text-2xl font-bold mt-1">DRIVE</div>
              <div className="text-xs text-neutral-400 mt-1">Precisão de competição</div>
            </div>
            <div className="glass rounded-2xl p-5">
              <div className="overline text-white/70">Realidade</div>
              <div className="font-display text-2xl font-bold mt-1">VIRTUAL</div>
              <div className="text-xs text-neutral-400 mt-1">Imersão total</div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <div className="overline text-xs text-neutral-400">Scroll</div>
        <div className="h-10 w-[1px] bg-gradient-to-b from-white/60 to-transparent" />
      </motion.div>
    </section>
  );
}
